/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Spin, Empty } from 'antd';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DraggableProjectRow } from './DraggableProjectRow';

interface ProjectBuilderTableProps {
  folderTree: any[];
  duplicateFolderIds: Set<number>;
  expandedFolders: Set<number>;
  onToggleExpand: (id: number) => void;
  onNest: (draggedId: number, targetId: number | null) => void;
  onRemove: (folderId: number) => void;
  canNest: (draggedId: number, targetId: number) => boolean;
  isLoading: boolean;
  rootDropZoneRef?: React.RefObject<HTMLDivElement>;
  onContextMenu?: (folder: any) => void;
  rightClickedFolderId?: number | null;
}

export const ProjectBuilderTable: React.FC<ProjectBuilderTableProps> = ({
  folderTree,
  duplicateFolderIds,
  expandedFolders,
  onToggleExpand,
  onNest,
  onRemove,
  canNest,
  isLoading,
  rootDropZoneRef,
  onContextMenu,
  rightClickedFolderId,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll logic for smooth scrolling during drag
  useEffect(() => {
    let rafId: number;
    let isScrolling = false;
    let lastClientY = 0;

    const handleDragMove = (e: DragEvent) => {
      if (!tableContainerRef.current) return;

      lastClientY = e.clientY;
      const container = tableContainerRef.current;
      const { top, bottom } = container.getBoundingClientRect();

      // Check if cursor is over root drop zone
      if (rootDropZoneRef?.current) {
        const dropZoneRect = rootDropZoneRef.current.getBoundingClientRect();
        const isOverDropZone =
          e.clientX >= dropZoneRect.left &&
          e.clientX <= dropZoneRect.right &&
          e.clientY >= dropZoneRect.top &&
          e.clientY <= dropZoneRect.bottom;

        // Don't scroll if over drop zone
        if (isOverDropZone) {
          isScrolling = false;
          if (rafId) cancelAnimationFrame(rafId);
          return;
        }
      }

      const scrollZone = 60;
      const scrollSpeed = 8;

      const autoScroll = () => {
        if (!isScrolling || !tableContainerRef.current) return;

        const container = tableContainerRef.current;

        if (lastClientY - top < scrollZone && container.scrollTop > 0) {
          container.scrollTop -= scrollSpeed;
          rafId = requestAnimationFrame(autoScroll);
        } else if (bottom - lastClientY < scrollZone) {
          const maxScroll = container.scrollHeight - container.clientHeight;
          if (container.scrollTop < maxScroll) {
            container.scrollTop += scrollSpeed;
            rafId = requestAnimationFrame(autoScroll);
          }
        } else {
          isScrolling = false;
        }
      };

      if (lastClientY - top < scrollZone || bottom - lastClientY < scrollZone) {
        if (!isScrolling) {
          isScrolling = true;
          autoScroll();
        }
      } else {
        isScrolling = false;
        if (rafId) cancelAnimationFrame(rafId);
      }
    };

    const handleDragEnd = () => {
      isScrolling = false;
      if (rafId) cancelAnimationFrame(rafId);
    };

    document.addEventListener('dragover', handleDragMove);
    document.addEventListener('dragend', handleDragEnd);
    document.addEventListener('drop', handleDragEnd);

    return () => {
      document.removeEventListener('dragover', handleDragMove);
      document.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('drop', handleDragEnd);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  if (isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (folderTree.length === 0) {
    return (
      <div css={styles.emptyContainer}>
        <Empty
          description={intl.formatMessage({ id: 'no-folders-yet' })}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div ref={tableContainerRef} css={styles.tableContainer}>
      <table css={styles.table}>
        <thead>
          <tr>
            <th style={{ width: '33.3%' }}>
              {intl.formatMessage({ id: 'folder-structure' })}
            </th>
            <th style={{ width: '66.6%' }}>
              {intl.formatMessage({ id: 'description' })}
            </th>
            <th style={{ width: '10%', textAlign: 'center' }}>
              {intl.formatMessage({ id: 'actions' })}
            </th>
          </tr>
        </thead>
        <tbody>
          {folderTree.map((folder, index) => (
            <DraggableProjectRow
              key={folder.id}
              folder={folder}
              level={0}
              isLastChild={index === folderTree.length - 1}
              parentChain={[]}
              isNew={folder.isNew || false}
              isDuplicate={duplicateFolderIds.has(folder.id)}
              duplicateFolderIds={duplicateFolderIds}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
              onNest={onNest}
              onRemove={onRemove}
              canNest={canNest}
              children={folder.children}
              onContextMenu={onContextMenu}
              rightClickedFolderId={rightClickedFolderId}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 120px)',
    overflow: 'auto',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 8,
    position: 'relative',

    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: theme.colors.backgroundSecondary || '#f5f5f5',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.border || '#d9d9d9',
      borderRadius: 4,
      '&:hover': {
        background: theme.colors.textSecondary || '#bfbfbf',
      },
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,

    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: theme.colors.backgroundSecondary || '#fafafa',
    },
    '& th': {
      backgroundColor: theme.colors.backgroundSecondary || '#fafafa',
      fontWeight: 600,
      borderBottom: `2px solid ${theme.colors.border}`,
      padding: '12px 16px',
      textAlign: 'left',
      color: theme.colors.text.primary,
    },
    '& td': {
      padding: '0',
      borderBottom: `1px solid ${theme.colors.border}`,
      height: 42,
      '&:first-of-type': {
        paddingLeft: 16,
      },
      '&:nth-of-type(2)': {
        padding: '8px 16px',
      },
      '&:last-of-type': {
        padding: '8px 16px',
      },
    },
    '& .draggable-row': {
      transition: 'all 0.2s ease',
      cursor: 'grab',
      '&:hover': {
        backgroundColor: theme.colors.primaryLight || '#f0f5ff',
        '& .drag-handle': {
          opacity: 1,
        },
      },
      '&.dragging': {
        opacity: 0.5,
        cursor: 'grabbing',
        backgroundColor: theme.colors.backgroundSecondary || '#f5f5f5',
      },
      '&.drop-target': {
        backgroundColor: '#f6ffed',
        '& > td:first-child': {
          borderLeft: `3px solid ${theme.colors.success || '#52c41a'}`,
        },
        boxShadow: 'inset 0 0 0 1px rgba(82, 196, 26, 0.2)',
      },
      '&.existing-folder': {
        backgroundColor: theme.colors.backgroundDisabled || '#f5f5f5',
        cursor: 'default',
        '&:hover': {
          backgroundColor: theme.colors.backgroundDisabled || '#f5f5f5',
        },
        '&.drop-target': {
          backgroundColor: '#f6ffed',
          '& > td:first-child': {
            borderLeft: `3px solid ${theme.colors.success || '#52c41a'}`,
          },
        },
      },
    },
    '& .drag-handle': {
      opacity: 0.3,
      transition: 'opacity 0.2s',
      cursor: 'grab',
      fontSize: 12,
      color: theme.colors.textSecondary,
    },
  },
});
